import type { ProposalConflict } from "work/proposals/negotiation/proposal-collaboration-contract";

/**
 * The minimum a semantic address needs to be labelled. Conflicts, change lines, and resolution
 * targets all carry it, so one label derivation serves every negotiation surface.
 */
export type ProposalSemanticTarget = {
  path: string;
  conflictKey: string;
};

/**
 * Turns a negotiation conflict's semantic path into a field name a user
 * recognises: `/feeSchedule` reads as "Fee Schedule", `/clientTeam/leader` as
 * "Client Team Leader".
 *
 * This derives the label from the path rather than mapping every known field
 * because the conflict contract is open-ended — the API may report a path this
 * build has never heard of, and a degraded-but-correct label beats "unknown field".
 */
export function proposalConflictFieldLabel(conflict: ProposalSemanticTarget): string {
  const label = conflict.path
    .split("/")
    .filter(Boolean)
    .flatMap((segment) => segment.replace(/([a-z0-9])([A-Z])/g, "$1 $2").split(" "))
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return label || conflict.conflictKey;
}

/**
 * Builds the message shown when a lifecycle command is rejected for content
 * conflicts. Naming the fields is the difference between "it failed" and a
 * user knowing what to look at, so the summary always names them.
 */
export function describeProposalConflicts(conflicts: readonly ProposalConflict[]): string | undefined {
  if (conflicts.length === 0) return undefined;

  const fields = [...new Set(conflicts.map(proposalConflictFieldLabel))];
  const subject = fields.length === 1 ? `${fields[0]} was` : `${fields.join(", ")} were`;
  return `${subject} changed by someone else while you were editing. Review the updated values, then try again.`;
}
